// Lo que la escena enseña de cada agente (insignia sobre la cabeza, punto de la etiqueta, frase de la tarjeta y si le toca
// a quien mira). Lo resuelve quien monta la escena (Escenario: marcadorEscena + de quién es); la escena solo lo pinta.
import { useMemo, useRef } from 'react';
import type { LucideIcon } from 'lucide-react';
import { shallow } from 'zustand/shallow';
import type { ContratadoDTO } from '../compartido/vocabulario';

export interface MarcadorMundo {
  /** Le toca a quien mira: solo esto cuenta para «↓ te necesita» y para dónde arranca la cámara (nunca lo de otro). */
  destaca: boolean;
  icono: LucideIcon | null;
  /** Clases de color de la insignia (fondo sólido + tinta del icono); null sin insignia. La etiqueta nunca se rellena:
   *  la urgencia la lleva la insignia, como en la pantalla de inicio del iPhone (visual.md §2.B-C). */
  insignia: string | null;
  punto: string | null;
  texto: string;
  /** De otra persona (nombre de pila del dueño): su etiqueta va apagada y dice de quién es. */
  de?: string;
  /** Aparcado (Pau aprobó «Aparcar» el 04-11): duerme en su mesa, sin insignia ni punto. */
  aparcado?: boolean;
  /**
   * 04-11 (mesas de equipo, regla 9) — su grupo espera a Santi y a él no le toca nada: apagado en su mesa (muñeco medio
   * transparente, etiqueta apagada). Lo urgente nunca va apagado (regla 10): ya no está en «Con Santi».
   */
  apagado?: boolean;
}

/**
 * De lo que da la lista (marco/situacion.ts `marcadorEscena`, el mismo «¿qué me toca?») a lo que pinta la escena: su
 * `relleno` (el color de su situación) es el de la insignia; la etiqueta ya no se rellena (visual.md §2.B).
 */
export function aMarcadorMundo(m: { destaca: boolean; icono: LucideIcon | null; relleno: string | null; punto: string | null; texto: string; aparcado?: boolean; apagado?: boolean }): MarcadorMundo {
  return { destaca: m.destaca, icono: m.icono, insignia: m.relleno, punto: m.punto, texto: m.texto, ...(m.aparcado ? { aparcado: true } : {}), ...(m.apagado ? { apagado: true } : {}) };
}

/**
 * El marcador de cada agente, calculado UNA vez por cambio de la lista. Si el de un agente no cambia, se devuelve el
 * MISMO objeto que la vez anterior: así su mesa (React.memo) no se vuelve a pintar por el aviso de otro.
 */
export function useMarcadores(lista: readonly ContratadoDTO[], marcador: (c: ContratadoDTO) => MarcadorMundo): ReadonlyMap<string, MarcadorMundo> {
  const anterior = useRef<ReadonlyMap<string, MarcadorMundo>>(new Map());
  return useMemo(() => {
    const marcas = new Map<string, MarcadorMundo>();
    for (const c of lista) {
      const nuevo = marcador(c);
      const viejo = anterior.current.get(c.agentId);
      marcas.set(c.agentId, viejo && shallow(viejo, nuevo) ? viejo : nuevo);
    }
    anterior.current = marcas;
    return marcas;
  }, [lista, marcador]);
}
